<template>
  <div class="container-fluid p-5 position-relative vh-100">
    <div class="d-flex justify-content-center">
      <Brand :width="150" withChat />
      <Logout class="position-absolute start-0 top-0"/>
    </div>
    <Button :label="$t('Consultation')" style="right: 50px;" route-name="ConsultingSearch">
      <img class="me-2" src="@/assets/icons/search-white.svg" width="20" alt="bx-search">
    </Button>
    <Button v-if="isManager" :label="$t('Unassociated projects')" style="right: 230px;" route-name="ManagerNonAssociated">
      <img class="me-2" src="@/assets/icons/link-slash.svg" width="20" alt="bx-search" />
    </Button>
    <div v-if="!isManager" class="alert alert-primary mt-4">
      {{ $t('Access to projects review is reserved for managers only.') }}
    </div>
    <div class="p-4" v-if="isManager">
      <div class="mb-3">
        <h4>{{ $t('Project search') }}</h4>
        <p class="text-muted mb-2">
          {{ $t('You can search for a project by entering the prescription code directly or selecting a date.') }}
        </p>
      </div>
      <GlobalSearch
        class="mb-4"
        :placeholder="$t('Search by prescription code')"
        :debounceTime="500"
        :minCharacters="3"
        @toSearch="searchByPrescriptionCode"
        :reset="searchBarReset"
      />
      <div class="mb-4">
        <Multiselect
          class="multiselect-violet"
          :close-on-select="true"
          :clear-on-select="false"
          :max="1"
          mode="single"
          :placeholder="$t('Choose a date')"
          v-model="selectedDate"
          :options="dates"
          :reset="false"
        />
      </div>
      <div v-if="selectedDate" class="mb-4">
        <input
          type="text"
          class="form-control"
          v-model="searchQuery"
          :placeholder="$t('Search by file name')"
          @input="currentPage = 1"
        />
      </div>
      <Spinner v-if="loading" />
      <div v-if="filteredProjects.length">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>{{ $t('Project') }}</th>
              <th>{{ $t('Software') }}</th>
              <th>{{ $t('Department') }}</th>
              <th>{{ $t('Prescription Code') }}</th>
              <th>{{ $t('Download') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="project in paginatedProjects" :key="project.key">
              <td><small>{{ project.id }}</small></td>
              <td><small :title="project.file_name">{{ truncate(project.file_name, 50) }}</small></td>
              <td><small>{{ project.software }}</small></td>
              <td><small>{{ project.department }}</small></td>
              <td><small>{{ project.numberText }}</small></td>
              <td>
                <img
                  class="invert cursor-pointer"
                  src="@/assets/icons/download.svg"
                  width="20"
                  height="20"
                  alt="download"
                  @click="handleDownload(project.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination
          v-if="totalPages > 1"
          :array="filteredProjects"
          :pageNumber="currentPage"
          :totalPages="totalPages"
          @increment="currentPage++"
          @decrement="currentPage--"
        />
      </div>
      <div v-else-if="selectedDate && !filteredProjects.length" class="alert alert-info">
        {{ $t('No associated projects found for the selected date') }}
      </div>
      <div v-if="error && error === t('No project found for this prescription code')" class="alert alert-info mt-3">
        {{ error }}
      </div>
      <div v-else-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import Brand from '@/components/Brand.vue';
import Logout from '@/components/Logout.vue';
import Button from '@/components/general/Button.vue';
import Spinner from '@/components/general/Spinner.vue';
import Pagination from '@/components/general/Pagination.vue';
import Multiselect from '@vueform/multiselect';
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { fetchProjects } from '@/use/repositories/projects/fetchProjects';
import { useI18n } from 'vue-i18n';
import { downloadProject } from '@/use/repositories/projects/downloadProject';
import { success } from '@/use/toast/success';
import { error as danger } from '@/use/toast/error';
import GlobalSearch from '@/components/GlobalSearch.vue';

export default {
  name: "QualityView",
  components: { Brand, Logout, Button, Spinner, Pagination, Multiselect, GlobalSearch },
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const isManager = computed(() => store.state.mainRole?.role?.slug === 'manager');
    const loading = ref(false);
    const error = ref(null);
    const dates = ref([]);
    const selectedDate = ref(null);
    const projects = ref([]);
    const currentPage = ref(1);
    const searchQuery = ref('');
    const itemsPerPage = 20;
    const searchBarReset = ref(false);

    const truncate = (str, n) => (str.length > n ? str.slice(0, n - 1) + '…' : str);

    const loadDates = async () => {
      loading.value = true;
      error.value = null;
      try {
        const { dates: fetchedDates } = await fetchProjects('associated', null, null, 1);
        dates.value = fetchedDates.map((date) => ({
          value: date,
          label: date.split('-').reverse().join('/'),
        }));
      } catch (err) {
        error.value = 'Errore nel caricamento delle date';
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const loadAssociatedProjects = async () => {
      if (!selectedDate.value) {
        projects.value = [];
        return;
      }
      loading.value = true;
      error.value = null;
      try {
        const { projects: fetchedProjects } = await fetchProjects('associated', null, selectedDate.value, 0);
        projects.value = fetchedProjects.map((project) => ({
          ...project,
          key: `${project.file_name}-${project.created_at}`,
          numberText: project.prescription_number_text || '',
        }));
      } catch (err) {
        error.value = 'Errore nel caricamento dei progetti associati';
        console.error(err);
      } finally {
        loading.value = false;
      }
      currentPage.value = 1;
    };

    const filteredProjects = computed(() => {
      if (!searchQuery.value) return projects.value;
      return projects.value.filter((p) =>
        p.file_name.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const totalPages = computed(() => Math.ceil(filteredProjects.value.length / itemsPerPage));
    const paginatedProjects = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      return filteredProjects.value.slice(start, start + itemsPerPage);
    });

    const handleDownload = async (projectId) => {
      try {
        const { downloadUrl, fileName } = await downloadProject(projectId);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        success(t('Project downloaded successfully'));
      } catch (err) {
        danger(t('Error downloading project'));
      }
    };

    const searchByPrescriptionCode = async (code) => {
      selectedDate.value = null;
      searchBarReset.value = false;
      if (!code || code.length < 3) return;
      loading.value = true;
      error.value = null;
      try {
        const { projects: foundProjects } = await fetchProjects('by_number_text', code, null, 0);
        if (foundProjects && foundProjects.length > 0) {
          projects.value = foundProjects;
        } else {
          error.value = t('No project found for this prescription code');
        }
      } catch (err) {
        error.value = t('Error searching project by prescription code');
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadDates);

    watch(selectedDate, () => {
      loadAssociatedProjects();
      searchBarReset.value = true;
    });

    return {
      loading,
      error,
      dates,
      selectedDate,
      projects,
      currentPage,
      searchQuery,
      filteredProjects,
      totalPages,
      paginatedProjects,
      loadAssociatedProjects,
      truncate,
      isManager,
      t,
      handleDownload,
      searchByPrescriptionCode,
      searchBarReset,
    };
  },
};
</script>

<style scoped>
.invert {
  filter: invert(1);
}
</style>
